// Canonical {code, display-name} pairs for the Hà Nội ward-level units
// (đơn vị hành chính cấp xã) the product supports. Used by the property
// create / edit wizard, the advanced-filter modal, the lead form, the
// project form, and any future picker (all through <WardCombobox>).
//
// Source of truth lives in the backend `wards` table (seeded by Flyway —
// V202605101200__wards_lookup.sql + V202608220002__wards_hanoi_2025.sql).
// This client-side list must stay in sync; when the backend exposes a
// /wards endpoint we can fetch + cache and delete this constant.
//
// The list follows the post-2025-07-01 administrative reform (Nghị quyết
// 1656/NQ-UBTVQH15): Hà Nội has 126 ward-level units — 51 phường (urban)
// and 75 xã (rural) — and no district level any more. Two pre-reform
// names survive as `khu_vuc` (market areas brokers still search by):
// "Mỹ Đình" and "Nam Từ Liêm", whose territory is officially phường
// Từ Liêm / Tây Mỗ / Đại Mỗ / Xuân Phương today. Keeping them selectable
// also keeps pre-existing listings valid.

export interface WardOption {
  value: string;
  label: string;
  /** phường (urban) / xã (rural) official units; khu_vuc = legacy market area. */
  kind: "phuong" | "xa" | "khu_vuc";
}

export const WARD_OPTIONS: readonly WardOption[] = [
  { value: "BA_DINH", label: "Ba Đình", kind: "phuong" },
  { value: "BACH_MAI", label: "Bạch Mai", kind: "phuong" },
  { value: "BO_DE", label: "Bồ Đề", kind: "phuong" },
  { value: "CAU_GIAY", label: "Cầu Giấy", kind: "phuong" },
  { value: "CHUONG_MY", label: "Chương Mỹ", kind: "phuong" },
  { value: "CUA_NAM", label: "Cửa Nam", kind: "phuong" },
  { value: "DUONG_NOI", label: "Dương Nội", kind: "phuong" },
  { value: "DAI_MO", label: "Đại Mỗ", kind: "phuong" },
  { value: "DINH_CONG", label: "Định Công", kind: "phuong" },
  { value: "DONG_NGAC", label: "Đông Ngạc", kind: "phuong" },
  { value: "DONG_DA", label: "Đống Đa", kind: "phuong" },
  { value: "GIANG_VO", label: "Giảng Võ", kind: "phuong" },
  { value: "HA_DONG", label: "Hà Đông", kind: "phuong" },
  { value: "HAI_BA_TRUNG", label: "Hai Bà Trưng", kind: "phuong" },
  { value: "HOAN_KIEM", label: "Hoàn Kiếm", kind: "phuong" },
  { value: "HOANG_LIET", label: "Hoàng Liệt", kind: "phuong" },
  { value: "HOANG_MAI", label: "Hoàng Mai", kind: "phuong" },
  { value: "HONG_HA", label: "Hồng Hà", kind: "phuong" },
  { value: "KHUONG_DINH", label: "Khương Đình", kind: "phuong" },
  { value: "KIEN_HUNG", label: "Kiến Hưng", kind: "phuong" },
  { value: "KIM_LIEN", label: "Kim Liên", kind: "phuong" },
  { value: "LANG", label: "Láng", kind: "phuong" },
  { value: "LINH_NAM", label: "Lĩnh Nam", kind: "phuong" },
  { value: "LONG_BIEN", label: "Long Biên", kind: "phuong" },
  { value: "NGHIA_DO", label: "Nghĩa Đô", kind: "phuong" },
  { value: "NGOC_HA", label: "Ngọc Hà", kind: "phuong" },
  { value: "O_CHO_DUA", label: "Ô Chợ Dừa", kind: "phuong" },
  { value: "PHU_DIEN", label: "Phú Diễn", kind: "phuong" },
  { value: "PHU_LUONG", label: "Phú Lương", kind: "phuong" },
  { value: "PHU_THUONG", label: "Phú Thượng", kind: "phuong" },
  { value: "PHUC_LOI", label: "Phúc Lợi", kind: "phuong" },
  { value: "PHUONG_LIET", label: "Phương Liệt", kind: "phuong" },
  { value: "SON_TAY", label: "Sơn Tây", kind: "phuong" },
  { value: "TAY_HO", label: "Tây Hồ", kind: "phuong" },
  { value: "TAY_MO", label: "Tây Mỗ", kind: "phuong" },
  { value: "TAY_TUU", label: "Tây Tựu", kind: "phuong" },
  { value: "THANH_LIET", label: "Thanh Liệt", kind: "phuong" },
  { value: "THANH_XUAN", label: "Thanh Xuân", kind: "phuong" },
  { value: "THUONG_CAT", label: "Thượng Cát", kind: "phuong" },
  { value: "TUNG_THIEN", label: "Tùng Thiện", kind: "phuong" },
  { value: "TU_LIEM", label: "Từ Liêm", kind: "phuong" },
  { value: "TUONG_MAI", label: "Tương Mai", kind: "phuong" },
  { value: "VAN_MIEU_QUOC_TU_GIAM", label: "Văn Miếu - Quốc Tử Giám", kind: "phuong" },
  { value: "VIET_HUNG", label: "Việt Hưng", kind: "phuong" },
  { value: "VINH_HUNG", label: "Vĩnh Hưng", kind: "phuong" },
  { value: "VINH_TUY", label: "Vĩnh Tuy", kind: "phuong" },
  { value: "XUAN_DINH", label: "Xuân Đỉnh", kind: "phuong" },
  { value: "XUAN_PHUONG", label: "Xuân Phương", kind: "phuong" },
  { value: "YEN_HOA", label: "Yên Hòa", kind: "phuong" },
  { value: "YEN_NGHIA", label: "Yên Nghĩa", kind: "phuong" },
  { value: "YEN_SO", label: "Yên Sở", kind: "phuong" },
  { value: "AN_KHANH", label: "An Khánh", kind: "xa" },
  { value: "BA_VI", label: "Ba Vì", kind: "xa" },
  { value: "BAT_TRANG", label: "Bát Tràng", kind: "xa" },
  { value: "BAT_BAT", label: "Bất Bạt", kind: "xa" },
  { value: "BINH_MINH", label: "Bình Minh", kind: "xa" },
  { value: "CHUYEN_MY", label: "Chuyên Mỹ", kind: "xa" },
  { value: "CHUONG_DUONG", label: "Chương Dương", kind: "xa" },
  { value: "CO_DO", label: "Cổ Đô", kind: "xa" },
  { value: "DAN_HOA", label: "Dân Hòa", kind: "xa" },
  { value: "DUONG_HOA", label: "Dương Hòa", kind: "xa" },
  { value: "DA_PHUC", label: "Đa Phúc", kind: "xa" },
  { value: "DAI_THANH", label: "Đại Thanh", kind: "xa" },
  { value: "DAI_XUYEN", label: "Đại Xuyên", kind: "xa" },
  { value: "DAN_PHUONG", label: "Đan Phượng", kind: "xa" },
  { value: "DOAI_PHUONG", label: "Đoài Phương", kind: "xa" },
  { value: "DONG_ANH", label: "Đông Anh", kind: "xa" },
  { value: "GIA_LAM", label: "Gia Lâm", kind: "xa" },
  { value: "HA_BANG", label: "Hạ Bằng", kind: "xa" },
  { value: "HAT_MON", label: "Hát Môn", kind: "xa" },
  { value: "HOA_LAC", label: "Hòa Lạc", kind: "xa" },
  { value: "HOA_PHU", label: "Hòa Phú", kind: "xa" },
  { value: "HOA_XA", label: "Hòa Xá", kind: "xa" },
  { value: "HOAI_DUC", label: "Hoài Đức", kind: "xa" },
  { value: "HONG_SON", label: "Hồng Sơn", kind: "xa" },
  { value: "HONG_VAN", label: "Hồng Vân", kind: "xa" },
  { value: "HUNG_DAO", label: "Hưng Đạo", kind: "xa" },
  { value: "HUONG_SON", label: "Hương Sơn", kind: "xa" },
  { value: "KIEU_PHU", label: "Kiều Phú", kind: "xa" },
  { value: "KIM_ANH", label: "Kim Anh", kind: "xa" },
  { value: "LIEN_MINH", label: "Liên Minh", kind: "xa" },
  { value: "ME_LINH", label: "Mê Linh", kind: "xa" },
  { value: "MINH_CHAU", label: "Minh Châu", kind: "xa" },
  { value: "MY_DUC", label: "Mỹ Đức", kind: "xa" },
  { value: "NAM_PHU", label: "Nam Phù", kind: "xa" },
  { value: "NGOC_HOI", label: "Ngọc Hồi", kind: "xa" },
  { value: "NOI_BAI", label: "Nội Bài", kind: "xa" },
  { value: "O_DIEN", label: "Ô Diên", kind: "xa" },
  { value: "PHU_CAT", label: "Phú Cát", kind: "xa" },
  { value: "PHU_NGHIA", label: "Phú Nghĩa", kind: "xa" },
  { value: "PHU_XUYEN", label: "Phú Xuyên", kind: "xa" },
  { value: "PHU_DONG", label: "Phù Đổng", kind: "xa" },
  { value: "PHUC_LOC", label: "Phúc Lộc", kind: "xa" },
  { value: "PHUC_SON", label: "Phúc Sơn", kind: "xa" },
  { value: "PHUC_THINH", label: "Phúc Thịnh", kind: "xa" },
  { value: "PHUC_THO", label: "Phúc Thọ", kind: "xa" },
  { value: "PHUONG_DUC", label: "Phượng Dực", kind: "xa" },
  { value: "QUANG_MINH", label: "Quang Minh", kind: "xa" },
  { value: "QUANG_BI", label: "Quảng Bị", kind: "xa" },
  { value: "QUANG_OAI", label: "Quảng Oai", kind: "xa" },
  { value: "QUOC_OAI", label: "Quốc Oai", kind: "xa" },
  { value: "SOC_SON", label: "Sóc Sơn", kind: "xa" },
  { value: "SON_DONG", label: "Sơn Đồng", kind: "xa" },
  { value: "SUOI_HAI", label: "Suối Hai", kind: "xa" },
  { value: "TAM_HUNG", label: "Tam Hưng", kind: "xa" },
  { value: "TAY_PHUONG", label: "Tây Phương", kind: "xa" },
  { value: "THACH_THAT", label: "Thạch Thất", kind: "xa" },
  { value: "THANH_OAI", label: "Thanh Oai", kind: "xa" },
  { value: "THANH_TRI", label: "Thanh Trì", kind: "xa" },
  { value: "THIEN_LOC", label: "Thiên Lộc", kind: "xa" },
  { value: "THUAN_AN", label: "Thuận An", kind: "xa" },
  { value: "THU_LAM", label: "Thư Lâm", kind: "xa" },
  { value: "THUONG_TIN", label: "Thường Tín", kind: "xa" },
  { value: "THUONG_PHUC", label: "Thượng Phúc", kind: "xa" },
  { value: "TIEN_THANG", label: "Tiến Thắng", kind: "xa" },
  { value: "TRAN_PHU", label: "Trần Phú", kind: "xa" },
  { value: "TRUNG_GIA", label: "Trung Giã", kind: "xa" },
  { value: "UNG_HOA", label: "Ứng Hòa", kind: "xa" },
  { value: "UNG_THIEN", label: "Ứng Thiên", kind: "xa" },
  { value: "VAN_DINH", label: "Vân Đình", kind: "xa" },
  { value: "VAT_LAI", label: "Vật Lại", kind: "xa" },
  { value: "VINH_THANH", label: "Vĩnh Thanh", kind: "xa" },
  { value: "XUAN_MAI", label: "Xuân Mai", kind: "xa" },
  { value: "YEN_BAI", label: "Yên Bài", kind: "xa" },
  { value: "YEN_LANG", label: "Yên Lãng", kind: "xa" },
  { value: "YEN_XUAN", label: "Yên Xuân", kind: "xa" },
  // Legacy market areas (pre-2025 names still used in listings + search).
  { value: "MY_DINH", label: "Mỹ Đình", kind: "khu_vuc" },
  { value: "NAM_TU_LIEM", label: "Nam Từ Liêm", kind: "khu_vuc" },
] as const;

/** Convenience lookup: ward code → display name. */
export const WARD_LABELS: Record<string, string> = Object.fromEntries(
  WARD_OPTIONS.map((w) => [w.value, w.label]),
);

/** Returns the display name for a ward code, falling back to the code itself. */
export function wardLabel(code: string | null | undefined): string {
  if (code === null || code === undefined || code === "") return "";
  return WARD_LABELS[code] ?? code;
}

/**
 * Lower-case and strip Vietnamese diacritics ("Mỹ Đình" → "my dinh") so
 * pickers can match queries typed without accents. NFD splits base letters
 * from combining marks; đ/Đ need their own mapping.
 */
export function deburrVi(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}
